import React from "react";
import { useNavigate } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import PageShell from "../components/PageShell";
import { BookingCTA, CTAButton, Section } from "../components/MarketingPrimitives";
import { serviceEngagements } from "../content/siteContent";

const Services = () => {
  const navigate = useNavigate();

  return (
    <PageShell>
      <PageMeta
        title="Services | BestSalesCoach.ai"
        description="BestSalesCoach.ai offers AI GTM Engineering Services for CROs, founders, and RevOps leaders who need practical workflows across signal, qualification, execution, and revenue visibility."
        canonicalPath="/services"
      />
      <Section
        id="services-hero"
        eyebrow="Services"
        title="AI GTM Engineering Services Powered by BestSalesCoach.ai"
        description="For CROs, founders, and RevOps leaders who need practical AI GTM workflows, platform guidance, and execution support without building a full internal GTM engineering function. Start small, prove the workflow, then expand across the GTM motion."
        tone="hero"
      >
        <div className="hero-actions">
          <BookingCTA
            label="Book a 20-Minute Services Call"
            eventName="services_call_click"
            sourcePage="services"
            ctaClicked="services_call"
            requestedOffer="AI GTM Engineering Services"
            buyerType="Services"
            urlType="services"
          />
          <CTAButton variant="secondary" onClick={() => navigate("/product")}>
            View Product
          </CTAButton>
        </div>
      </Section>

      <Section
        id="engagement-options"
        eyebrow="Engagement options"
        title="Scoped engagements that move from diagnosis to workflow execution"
        description="The focus is practical delivery: understand where execution is leaking, build one workflow that matters, measure adoption, and improve the motion."
        tone="light"
      >
        <div className="marketing-grid marketing-grid-two">
          {serviceEngagements.map((service) => (
            <article key={service.title} className="marketing-card">
              <p className="marketing-kicker">{service.duration}</p>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="marketing-bullet-list compact">
                {service.deliverables.map((deliverable) => (
                  <li key={deliverable}>{deliverable}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </Section>

      <Section
        id="how-we-start"
        eyebrow="How We Start"
        title="Start with one focused GTM execution workflow"
        description="Most teams begin with a focused GTM execution review or signal-to-opportunity sprint. We identify where account signals, MEDDICC evidence, stakeholder coverage, technical validation, and next-best actions are breaking down, then configure a practical workflow around one priority revenue motion."
        tone="dark"
      >
        <div className="marketing-section-header">
          <p className="marketing-section-description">
            Commercial options depend on team size, workflow scope, integrations, and whether you need
            product access, GTM engineering support, or both.
          </p>
        </div>
        <div className="marketing-grid marketing-grid-three">
          <article className="marketing-card">
            <h3>GTM Execution Review</h3>
            <p>
              Diagnose where signal, qualification, stakeholder coverage, MEDDICC evidence,
              technical validation, or handoffs are leaking.
            </p>
            <ul className="marketing-bullet-list compact">
              <li>Review ICP and account signal flow</li>
              <li>Identify qualification and handoff gaps</li>
              <li>Inspect MEDDICC and stakeholder evidence</li>
              <li>Recommend one priority workflow to improve first</li>
            </ul>
          </article>
          <article className="marketing-card">
            <h3>Signal-to-Opportunity Sprint</h3>
            <p>
              Build one workflow that turns account signals into briefs, scoring, stakeholder
              hypotheses, and next-best actions.
            </p>
            <ul className="marketing-bullet-list compact">
              <li>Define signal sources and ICP scoring logic</li>
              <li>Create account and opportunity briefs</li>
              <li>Map stakeholders and missing evidence</li>
              <li>Generate next-best actions for Sales and PreSales</li>
            </ul>
          </article>
          <article className="marketing-card">
            <h3>Product + GTM Engineering Support</h3>
            <p>
              Use BestSalesCoach.ai with guided implementation support for Sales, PreSales, RevOps,
              Customer Success, or founder-led GTM.
            </p>
            <ul className="marketing-bullet-list compact">
              <li>Configure workflow and review cadence</li>
              <li>Support adoption across GTM roles</li>
              <li>Improve signal-to-execution visibility</li>
              <li>Turn learnings into repeatable playbooks</li>
            </ul>
          </article>
        </div>
      </Section>

      <Section
        id="global-delivery"
        eyebrow="Remote delivery"
        title="Built for remote and global delivery"
        description="BestSalesCoach.ai services can be delivered remotely from Australia for APAC and global SaaS teams through advisory, implementation, or platform-led engagement structures. The focus is scoped outcomes: diagnose the motion, build the workflow, measure adoption, and improve execution."
        tone="dark"
      >
        <div className="hero-actions">
          <BookingCTA
            label="Book a 20-Minute Services Call"
            eventName="services_call_click"
            sourcePage="services"
            ctaClicked="services_call"
            requestedOffer="AI GTM Engineering Services"
            buyerType="Services"
            urlType="services"
          />
        </div>
      </Section>
    </PageShell>
  );
};

export default Services;
